"use client"

import type React from "react"
import { observer } from "mobx-react-lite"
import { useRef, useState } from "react"
import { toPng } from "html-to-image"
import {
  Download,
  Calendar,
  BarChart3,
  FileText,
  TrendingUp,
  RefreshCw,
  Mail,
  User,
  Target,
  CheckCircle2,
  XCircle,
  Award,
  Clock,
  AlertTriangle,
} from "lucide-react"
import { useAppContext } from "../../../shared/functions/Context"
import { generateCompanyDashboardPDF } from "../../../shared/functions/scorecard-pdf/GeneratePerformaneAgreementPDF"
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary"
import { cn } from "../../shared/utils/utils"

import { Badge } from "../company-dashboard/ui/badge"
import { Card, CardContent, CardHeader, CardTitle} from "../company-dashboard/ui/card"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../company-dashboard/ui/dropdown-menu"



import Modal from "../../../shared/components/Modal"
import MODAL_NAMES from "../../dialogs/ModalName"
import StrategicMapObjectiveModal from "../../dialogs/strategic-map-objective/StrategicMapObjectiveModal"
import { Button } from "../company-dashboard/ui/button"
import { Progress } from "../company-dashboard/ui/progress"

interface TabsProps {
  tab: "Q2" | "Q4"
  setTab: React.Dispatch<React.SetStateAction<"Q2" | "Q4">>
  handleExportPDF: () => Promise<void>
  handleExportExcel: () => Promise<void>
  handleExportDetailedReport: () => Promise<void>
  handleSendEmail: () => Promise<void>
  loading: boolean
  userName: string
}

const IndividualTabs = observer(
  ({
    tab,
    setTab,
    handleExportPDF,
    handleExportExcel,
    handleExportDetailedReport,
    handleSendEmail,
    loading,
    userName,
  }: TabsProps) => {
    const { store } = useAppContext()
    const [refreshing, setRefreshing] = useState(false)

    const handleRefresh = async () => {
      setRefreshing(true)
      setTimeout(() => setRefreshing(false), 1000)
    }

    return (
      <Card className="mb-6 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="pt-6 px-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-6">
            <div className="flex items-center space-x-4 flex-wrap gap-2">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">My Performance Dashboard</h1>
                  <p className="text-sm text-gray-600">Personal performance tracking and goal management</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Badge variant="outline" className="text-xs bg-white">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Personal Analytics
                </Badge>
                <Badge variant="outline" className="text-xs bg-white">
                  <Target className="w-3 h-3 mr-1" />
                  {store.measure.allMe.length} KPIs
                </Badge>
              </div>
            </div>

            <div className="flex items-center space-x-3 flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="bg-white">
                <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
                Refresh
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="bg-white" disabled={loading}>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                    {loading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current ml-2"></div>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Export Options</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportPDF}>
                    <FileText className="w-4 h-4 mr-2" />
                    PDF Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportExcel}>
                    <FileText className="w-4 h-4 mr-2" />
                    Excel Report
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportDetailedReport}>
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Detailed Analysis
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSendEmail}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send via Email
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <div className="flex bg-white rounded-lg p-1 shadow-sm border">
                <button
                  onClick={() => setTab("Q2")}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center space-x-2",
                    tab === "Q2"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                  )}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Semester 1 (Q2)</span>
                </button>
                <button
                  onClick={() => setTab("Q4")}
                  className={cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center space-x-2",
                    tab === "Q4"
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
                  )}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Semester 2 (Q4)</span>
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  },
)

const PersonalMetricsAnalytics = observer(({ tab }: { tab: "Q2" | "Q4" }) => {
  const { store } = useAppContext()
  const objectives = store.objective.allMe
  const measures = store.measure.allMe

  const ratingField = tab === "Q2" ? "finalRating" : "finalRating2"
  const redMeasures = measures.filter((measure) => (measure.asJson[ratingField] || 0) <= 2)
  const goodMeasures = measures.filter((measure) => (measure.asJson[ratingField] || 0) >= 3)
  const completedMeasures = measures.filter((measure) => measure.asJson[ratingField] !== null)

  const $measures = measures.map((measure) => measure.asJson)
  const avgRating =
    $measures.length > 0
      ? $measures.reduce((sum, measure) => sum + (measure[ratingField] || 0), 0) / $measures.length
      : 0

  const StatCard = ({
    value,
    label,
    icon,
    colorClass,
    subtitle,
  }: {
    value: string | number
    label: string
    icon: React.ReactNode
    colorClass: string
    subtitle?: string
  }) => (
    <Card className="flex items-center p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className={cn("p-3 rounded-full mr-4", colorClass)}>{icon}</div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-600">{label}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </Card>
  )

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <StatCard
        value={measures.length}
        label="Total KPIs"
        subtitle="All assigned measures"
        icon={<Target className="w-6 h-6 text-white" />}
        colorClass="bg-blue-500"
      />
      <StatCard
        value={completedMeasures.length}
        label="Completed KPIs"
        subtitle={`${measures.length > 0 ? Math.round((completedMeasures.length / measures.length) * 100) : 0}% completion`}
        icon={<CheckCircle2 className="w-6 h-6 text-white" />}
        colorClass="bg-green-500"
      />
      <StatCard
        value={redMeasures.length}
        label="Needs Attention"
        subtitle="Below target performance"
        icon={<AlertTriangle className="w-6 h-6 text-white" />}
        colorClass="bg-red-500"
      />
      <StatCard
        value={avgRating.toFixed(1)}
        label="Average Rating"
        subtitle="Overall performance score"
        icon={<Award className="w-6 h-6 text-white" />}
        colorClass={avgRating >= 3 ? "bg-green-500" : avgRating >= 2 ? "bg-yellow-500" : "bg-red-500"}
      />
    </div>
  )
})

const PersonalPerformanceTrend = observer(({ tab }: { tab: "Q2" | "Q4" }) => {
  const { store } = useAppContext()
  const measures = store.measure.allMe

  const quarterlyProgress = [
    {
      quarter: "Q1",
      completed: measures.filter((m) => m.asJson.quarter1Actual !== null && m.asJson.quarter1Actual !== 0).length,
      total: measures.length,
    },
    {
      quarter: "Q2",
      completed: measures.filter((m) => m.asJson.quarter2Actual !== null && m.asJson.quarter2Actual !== 0).length,
      total: measures.length,
    },
    {
      quarter: "Q3",
      completed: measures.filter((m) => m.asJson.quarter3Actual !== null && m.asJson.quarter3Actual !== 0).length,
      total: measures.length,
    },
    {
      quarter: "Q4",
      completed: measures.filter((m) => m.asJson.quarter4Actual !== null && m.asJson.quarter4Actual !== 0).length,
      total: measures.length,
    },
  ]

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Quarterly Progress Tracking
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {quarterlyProgress.map((quarter) => {
            const percentage = quarter.total > 0 ? (quarter.completed / quarter.total) * 100 : 0
            return (
              <div key={quarter.quarter} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{quarter.quarter}</span>
                  <span className="text-sm font-bold text-gray-900">
                    {quarter.completed}/{quarter.total} ({percentage.toFixed(0)}%)
                  </span>
                </div>
                <Progress value={percentage} className="h-3" />
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
})

const ObjectivePerformanceChart = observer(({ tab }: { tab: "Q2" | "Q4" }) => {
  const { store } = useAppContext()
  const objectives = store.objective.allMe

  const objectiveData = objectives
    .map((objective) => {
      const rating = tab === "Q2" ? objective.q2Rating : objective.q4Rating
      return {
        id: objective.asJson.id,
        description: objective.asJson.description,
        rating: rating.rate,
        isUpdated: rating.isUpdated,
        perspective: objective.asJson.perspective,
      }
    })
    .sort((a, b) => b.rating - a.rating)

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Target className="w-5 h-5 text-green-600" />
          Objective Performance Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {objectiveData.map((objective, index) => {
            const isTopPerformer = index < 3 && objectiveData.length > 3
            const isLowPerformer = objective.rating < 2

            return (
              <div
                key={objective.id}
                className={`p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${
                  isTopPerformer
                    ? "bg-green-50 border-green-200"
                    : isLowPerformer
                      ? "bg-red-50 border-red-200"
                      : "bg-gray-50 border-gray-200"
                }`}
                onClick={() => {
                  const obj = store.objective.allMe.find((o) => o.asJson.id === objective.id)
                  if (obj) {
                    store.objective.select(obj.asJson)
                    // showModalFromId(MODAL_NAMES.EXECUTION.MAP_OVERVIEW_MODAL)
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{objective.description}</p>
                    <p className="text-xs text-gray-500">{objective.perspective}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <div
                          key={star}
                          className={`w-3 h-3 rounded-full ${
                            star <= objective.rating ? "bg-yellow-400" : "bg-gray-200"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-bold text-gray-900">{objective.rating.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            )
          })}
          {objectiveData.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Target className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No objectives found. Please add objectives to track your performance.</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
})

const PerspectiveBreakdown = observer(({ tab }: { tab: "Q2" | "Q4" }) => {
  const { store } = useAppContext()
  const measures = store.measure.allMe

  const ratingField = tab === "Q2" ? "finalRating" : "finalRating2"
  const perspectives = ["Financial", "Customer", "Internal Process", "Learning & Growth"]

  const perspectiveData = perspectives.map((perspective) => {
    const perspectiveMeasures = measures.filter(
      (m) =>
        m.asJson.perspective === perspective ||
        (perspective === "Internal Process" && m.asJson.perspective === "Operational"),
    )

    const avgRating =
      perspectiveMeasures.length > 0
        ? perspectiveMeasures.reduce((sum, measure) => sum + (measure.asJson[ratingField] || 0), 0) /
          perspectiveMeasures.length
        : 0

    return {
      perspective,
      rating: avgRating,
      count: perspectiveMeasures.length,
    }
  })

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-600" />
          Performance by Perspective
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {perspectiveData.map((item) => {
            const percentage = (item.rating / 5) * 100
            const colorClass = item.rating >= 3 ? "bg-green-500" : item.rating >= 2 ? "bg-yellow-500" : "bg-red-500"

            return (
              <div key={item.perspective} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">{item.perspective}</span>
                  <div className="text-right">
                    <span className="text-sm font-bold text-gray-900">{item.rating.toFixed(1)}/5.0</span>
                    <span className="text-xs text-gray-500 ml-1">({item.count} KPIs)</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${colorClass}`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
})

const KPIStatusBreakdown = observer(({ tab }: { tab: "Q2" | "Q4" }) => {
  const { store } = useAppContext()
  const measures = store.measure.allMe

  const ratingField = tab === "Q2" ? "finalRating" : "finalRating2"
  const redMeasures = measures.filter((measure) => (measure.asJson[ratingField] || 0) < 2)
  const amberMeasures = measures.filter(
    (measure) => (measure.asJson[ratingField] || 0) >= 2 && (measure.asJson[ratingField] || 0) < 3,
  )
  const greenMeasures = measures.filter((measure) => (measure.asJson[ratingField] || 0) >= 3)

  const MeasuresList = ({
    title,
    measures: measuresList,
    icon,
    emptyMessage,
  }: {
    title: string
    measures: typeof measures
    icon: React.ReactNode
    emptyMessage: string
  }) => (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {measuresList.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">{emptyMessage}</p>
          ) : (
            measuresList.map((measure) => (
              <div
                key={measure.asJson.id}
                className="flex items-center justify-between p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{measure.asJson.description}</p>
                  <p className="text-xs text-gray-500">{measure.asJson.perspective}</p>
                </div>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div
                      key={star}
                      className={`w-2 h-2 rounded-full ${
                        star <= (measure.asJson[ratingField] || 0) ? "bg-yellow-400" : "bg-gray-200"
                      }`}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <MeasuresList
        title="Excellent Performance"
        measures={greenMeasures}
        icon={<CheckCircle2 className="w-5 h-5 text-green-500" />}
        emptyMessage="No excellent performing KPIs yet. Keep working towards your goals!"
      />
      <MeasuresList
        title="Needs Improvement"
        measures={amberMeasures}
        icon={<Clock className="w-5 h-5 text-yellow-500" />}
        emptyMessage="No KPIs in this category."
      />
      <MeasuresList
        title="Requires Attention"
        measures={redMeasures}
        icon={<XCircle className="w-5 h-5 text-red-500" />}
        emptyMessage="Great! No KPIs require immediate attention."
      />
    </div>
  )
})

const IndividualDashboardContent = observer(({ tab }: { tab: "Q2" | "Q4" }) => {
  return (
    <div className="space-y-6">
      <PersonalMetricsAnalytics tab={tab} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PersonalPerformanceTrend tab={tab} />
        <ObjectivePerformanceChart tab={tab} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PerspectiveBreakdown tab={tab} />
        <div className="space-y-6">{/* Additional space for future charts */}</div>
      </div>

      <KPIStatusBreakdown tab={tab} />
    </div>
  )
})

export const IndividualDashboard = observer(() => {
  const { ui, store } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [tab, setTab] = useState<"Q2" | "Q4">("Q2")
  const chartRef = useRef<HTMLDivElement>(null)

  const mission = store.visionmission.all[0]?.asJson.mission || ""
  const vision = store.visionmission.all[0]?.asJson.vision || ""
  const userName = store.auth.meJson?.displayName || "User"

 const handleExportPDF = async () => {
  if (chartRef.current === null) return;

  try {
    setExportLoading(true);

    const dataUrl = await toPng(chartRef.current, { cacheBust: true });

    await generateCompanyDashboardPDF(vision, mission, dataUrl); // pass dataUrl string directly

    ui.snackbar.load({
      id: Date.now(),
      message: "Dashboard exported successfully!",
      type: "success",
    });
  } catch (error) {
    ui.snackbar.load({
      id: Date.now(),
      message: "Error! Failed to export dashboard.",
      type: "danger",
    });
  } finally {
    setExportLoading(false);
  }
};


  const handleExportExcel = async () => {
    setExportLoading(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000))
      ui.snackbar.load({
        id: Date.now(),
        message: "Excel report exported successfully!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to export Excel.",
        type: "danger",
      })
    }
    setExportLoading(false)
  }

  const handleExportDetailedReport = async () => {
    setExportLoading(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 3000))
      ui.snackbar.load({
        id: Date.now(),
        message: "Detailed analysis report generated successfully!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to generate detailed report.",
        type: "danger",
      })
    }
    setExportLoading(false)
  }

  const handleSendEmail = async () => {
    setExportLoading(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 2000))
      ui.snackbar.load({
        id: Date.now(),
        message: "Dashboard sent successfully via email!",
        type: "success",
      })
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        message: "Error! Failed to send email.",
        type: "danger",
      })
    }
    setExportLoading(false)
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 sm:p-6 w-full">
        <div className="space-y-6">
          <IndividualTabs
            tab={tab}
            setTab={setTab}
            handleExportPDF={handleExportPDF}
            handleExportExcel={handleExportExcel}
            handleExportDetailedReport={handleExportDetailedReport}
            handleSendEmail={handleSendEmail}
            loading={exportLoading}
            userName={userName}
          />

          <div ref={chartRef}>
            <IndividualDashboardContent tab={tab} />
          </div>
        </div>
      </div>

      <ErrorBoundary>
        <Modal modalId={MODAL_NAMES.EXECUTION.MAP_OVERVIEW_MODAL} cssClass="uk-modal-container">
          <StrategicMapObjectiveModal />
        </Modal>
      </ErrorBoundary>
    </ErrorBoundary>
  )
})

export default IndividualDashboard
